import { Timestamp } from 'firebase/firestore';
import {
  DocumentTemplate,
  RECETA_MEDICATIONS_KEY,
  TEMPLATE_AUTO_LIST_PLACEHOLDERS,
  TEMPLATE_AUTO_PLACEHOLDERS,
  TEMPLATE_MANUAL_PLACEHOLDERS,
  TEMPLATE_PLACEHOLDERS,
  TemplateCustomField,
  TemplateFieldType,
  TemplatePlaceholderFillMode,
  extractPlaceholderKeys,
  formatTemplateListItems,
  isManualPlaceholderKey,
} from '../models/document-template.type';
import { Patient } from '../models/patient.type';
import { Prescription } from '../models/appointment.type';

export interface TemplateFillContext {
  patient: Patient | null;
  doctorLabel: string;
  businessName: string;
  dateLabel: string;
  /** Consulta prescriptions; fills the medicamentos list. */
  medications?: Prescription[];
}

export interface TemplateFillField {
  key: string;
  label: string;
  fillMode: TemplatePlaceholderFillMode;
  type: TemplateFieldType;
}

export function buildTemplateValues(
  context: TemplateFillContext
): Record<string, string> {
  const patient = context.patient;
  return {
    'paciente.nombre': patient?.name?.trim() ?? '',
    'paciente.dob': formatDob(patient?.dob),
    'paciente.edad': formatAge(patient?.dob),
    'paciente.address': patient?.address?.trim() ?? '',
    'paciente.phone': patient?.phone?.trim() ?? '',
    'doctor.label': context.doctorLabel.trim(),
    'consultorio.nombre': context.businessName.trim(),
    fecha: context.dateLabel.trim(),
    [RECETA_MEDICATIONS_KEY]: medicationsListText(context.medications ?? []),
  };
}

/** One line per medication: "name — Frecuencia: … · Duración: …". */
export function medicationsListText(medications: Prescription[]): string {
  return formatTemplateListItems(
    medications.map((medication) => {
      const details: string[] = [];
      if (medication.frequency?.trim()) {
        details.push(`Frecuencia: ${medication.frequency.trim()}`);
      }
      if (medication.duration?.trim()) {
        details.push(`Duración: ${medication.duration.trim()}`);
      }
      return {
        title: medication.name?.trim() ?? '',
        detail: details.join('   ·   '),
      };
    })
  );
}

export function collectTemplatePlaceholderKeys(
  template: DocumentTemplate
): string[] {
  const keys = new Set<string>();
  for (const block of template.blocks) {
    if (block.type === 'text') {
      for (const key of extractPlaceholderKeys(block.content)) {
        keys.add(key);
      }
    } else if (block.type === 'placeholder' || block.type === 'list') {
      keys.add(block.key);
    }
  }
  return [...keys];
}

/** Keys rendered by list blocks (or declared as list custom fields). */
export function collectTemplateListKeys(template: DocumentTemplate): Set<string> {
  const keys = new Set<string>();
  for (const block of template.blocks) {
    if (block.type === 'list') {
      keys.add(block.key);
    }
  }
  for (const field of template.customFields) {
    if (field.type === 'list') {
      keys.add(field.key);
    }
  }
  return keys;
}

/** True when the template includes doctor.label (or other doctor.* keys). */
export function templateUsesDoctorPlaceholder(
  template: DocumentTemplate
): boolean {
  return collectTemplatePlaceholderKeys(template).some(
    (key) => key === 'doctor.label' || key.startsWith('doctor.')
  );
}

export function formatDocumentDateLabel(date: Date = new Date()): string {
  return date.toLocaleDateString('es', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function listTemplateFillFields(
  template: DocumentTemplate
): TemplateFillField[] {
  const keys = collectTemplatePlaceholderKeys(template);
  const listKeys = collectTemplateListKeys(template);
  const fields = keys.map((key) => ({
    key,
    label: placeholderLabel(key, template.customFields),
    fillMode: isManualPlaceholderKey(key, template.customFields)
      ? ('manual' as const)
      : ('auto' as const),
    type: listKeys.has(key) ? ('list' as const) : ('text' as const),
  }));

  fields.sort((a, b) => {
    if (a.fillMode !== b.fillMode) {
      return a.fillMode === 'manual' ? -1 : 1;
    }
    return a.label.localeCompare(b.label, 'es', { sensitivity: 'base' });
  });
  return fields;
}

export function buildInitialFieldValues(
  template: DocumentTemplate,
  context: TemplateFillContext
): Record<string, string> {
  const auto = buildTemplateValues(context);
  const values: Record<string, string> = {};
  for (const key of collectTemplatePlaceholderKeys(template)) {
    values[key] = auto[key] ?? '';
  }
  return values;
}

/** Sample values for preview / example PDF (not real patient data). */
export function buildExampleFieldValues(
  template: DocumentTemplate,
  context: Partial<TemplateFillContext> = {}
): Record<string, string> {
  const base = buildTemplateValues({
    patient: context.patient ?? null,
    doctorLabel: context.doctorLabel?.trim() || 'Dra. Ejemplo',
    businessName: context.businessName?.trim() || 'Consultorio Ejemplo',
    dateLabel: context.dateLabel?.trim() || '9 de septiembre, 2026',
  });

  const samples: Record<string, string> = {
    ...base,
    'paciente.nombre': base['paciente.nombre'] || 'Juan Pérez García',
    'paciente.dob': base['paciente.dob'] || '15 de marzo de 1988',
    'paciente.edad': base['paciente.edad'] || '36 años',
    'paciente.address': base['paciente.address'] || 'Calle Principal 123',
    'paciente.phone': base['paciente.phone'] || '5555-1234',
    'libre.motivo': 'Control de seguimiento',
    'libre.diagnostico': 'Hipertensión arterial controlada',
    'libre.observaciones': 'Paciente estable, sin signos de alarma.',
    'libre.recomendaciones': 'Continuar tratamiento actual y control en 3 meses.',
    [RECETA_MEDICATIONS_KEY]: medicationsListText([
      { name: 'Paracetamol 500 mg', frequency: 'Cada 8 horas', duration: '5 días' },
      { name: 'Amoxicilina 500 mg', frequency: 'Cada 12 horas', duration: '7 días' },
    ]),
  };

  const listKeys = collectTemplateListKeys(template);
  const values: Record<string, string> = {};
  for (const key of collectTemplatePlaceholderKeys(template)) {
    if (samples[key]) {
      values[key] = samples[key];
      continue;
    }
    const custom = template.customFields.find((field) => field.key === key);
    if (custom && listKeys.has(key)) {
      values[key] = `${custom.label} 1\n${custom.label} 2`;
      continue;
    }
    if (custom) {
      values[key] = `Ejemplo: ${custom.label}`;
      continue;
    }
    values[key] = samples[key] || `Ejemplo (${key})`;
  }
  return values;
}

export function placeholderLabel(
  key: string,
  customFields: TemplateCustomField[] = []
): string {
  const custom = customFields.find((field) => field.key === key);
  if (custom?.label) {
    return custom.label;
  }
  return (
    TEMPLATE_PLACEHOLDERS.find((item) => item.key === key)?.label ??
    TEMPLATE_MANUAL_PLACEHOLDERS.find((item) => item.key === key)?.label ??
    TEMPLATE_AUTO_PLACEHOLDERS.find((item) => item.key === key)?.label ??
    TEMPLATE_AUTO_LIST_PLACEHOLDERS.find((item) => item.key === key)?.label ??
    key
  );
}

function formatDob(dob?: Timestamp): string {
  if (!dob) {
    return '';
  }
  return dob.toDate().toLocaleDateString('es', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Whole years from DOB to today, e.g. "36 años". */
function formatAge(dob?: Timestamp, now: Date = new Date()): string {
  if (!dob) {
    return '';
  }
  const birth = dob.toDate();
  let years = now.getFullYear() - birth.getFullYear();
  const monthDelta = now.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < birth.getDate())) {
    years -= 1;
  }
  if (years < 0) {
    return '';
  }
  return years === 1 ? '1 año' : `${years} años`;
}
